import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  ViewChild,
  computed
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { TabView } from 'primeng/tabview';
import { lastValueFrom, map } from 'rxjs';
import {
  Category,
  CategorySummaryDTO,
  ProductControllerService,
  ProductSummary,
  ProductVariant
} from 'src/app/admin-api';
import { TableColumn } from 'src/app/components/table';
import {
  Role,
  getAllBrandEquities,
  getEquityLabel,
  roleAsObject
} from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { CategoryUtil } from 'src/app/utils/category.util';

@Component({
  selector: 'app-category-detail',
  templateUrl: './category-detail.component.html',
  styleUrls: ['./category-detail.component.scss']
})
export class CategoryDetailComponent implements OnInit {
  @ViewChild(TabView)
  tabview: TabView | undefined;

  categoryId: number | undefined;
  category: Category | undefined;
  categories: Array<CategorySummaryDTO> | undefined;
  products: Array<ProductSummary> | undefined;
  productVariants: Array<ProductVariant> | undefined;
  selectedTab = 0;
  isMobile?: boolean;
  productCols = [
    new TableColumn('Id', 'externalId', true, 'number'),
    new TableColumn(
      'InternalEAN',
      'internalEAN',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn(
      'Nome',
      'productName',
      true,
      'text',
      '/products/catalog/product/',
      'productId',
      true,
      'contains'
    ),
    new TableColumn(
      'Marca',
      'brandName',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn(
      'BrandEquity',
      'brandEquityLabel',
      true,
      'text',
      undefined,
      undefined,
      true,
      'equals'
    ),
    new TableColumn.Builder()
      .setHeader('Categoria')
      .setField('categoryName')
      .setCondition('contains')
      .setDisplayFunction(
        (p: ProductSummary) => `${p.categoryExternalIdString} ${p.categoryName}`
      )
      .setRouterLink('/products/categories/')
      .setRouterLinkFieldName('categoryId')
      .setLinkActive(
        (p: ProductSummary) => p.categoryId !== this.category?.categoryId
      )
      .build(),
    new TableColumn('Nota', 'ratingAverage', false),
    new TableColumn('Avaliações', 'reviewCount', false),
    new TableColumn('Estoque', 'stock', false),
    new TableColumn(
      'Criação',
      'dateCreated',
      true,
      'date',
      undefined,
      undefined,
      true,
      'between'
    )
  ];
  variantCols: Array<TableColumn> = [
    new TableColumn(
      'Id',
      'externalId',
      true,
      'number',
      '/products/catalog/product-variant/',
      'productVariantId'
    ),
    new TableColumn(
      'Nome',
      'productVariantName',
      true,
      'text',
      '/products/catalog/product-variant/',
      'productVariantId',
      true,
      'contains'
    ),
    new TableColumn(
      'Marca',
      'brandName',
      true,
      'text',
      '/products/brands/',
      'brandId',
      true,
      'contains'
    ),
    new TableColumn(
      'InternalEAN',
      'internalEAN',
      true,
      'text',
      '/products/catalog/product-variant/',
      'productVariantId',
      true,
      'contains'
    ),
    new TableColumn('Peso (g)', 'weight', false, 'formattedNumber'),
    new TableColumn(
      'Validade',
      'expiration',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn(
      'Data cadastro',
      'dateCreated',
      true,
      'date',
      undefined,
      undefined,
      true,
      'between'
    ),
    new TableColumn(
      'Data atualização',
      'dateUpdated',
      true,
      'date',
      undefined,
      undefined,
      true,
      'between'
    )
  ];
  categoryCols: Array<TableColumn> = [
    new TableColumn(
      'Id',
      'categoryId',
      true,
      'number',
      '/products/categories/',
      'categoryId'
    ),
    new TableColumn(
      'Id Externo',
      'externalIdString',
      true,
      'text',
      '/products/categories/',
      'categoryId',
      true,
      'contains'
    ),
    new TableColumn(
      'Categoria',
      'category',
      true,
      'text',
      '/products/categories/',
      'categoryId',
      true,
      'contains'
    ),
    new TableColumn('Produtos', 'products', false, 'text')
  ];
  dropdownFilters = computed(() => {
    return {
      brandEquityLabel: [{ label: 'Todas', value: '-1' }].concat(
        getAllBrandEquities().map((e) => ({
          label: e.label,
          value: e.label
        }))
      )
    };
  });
  childrenCategories: CategorySummaryDTO[];
  categoriesList: CategorySummaryDTO[];

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkScreenSize();
  }
  permission = false;

  constructor(
    public productService: ProductControllerService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private title: Title,
    private ngxPermissionsService: NgxPermissionsService
  ) {}

  checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(async (params) => {
      this.permission = await this.ngxPermissionsService.hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue,
        roleAsObject(Role.Admin_Allocation).enumValue,
        roleAsObject(Role.User_Allocation).enumValue
      ]);
      if (params['categoryId'] && params['categoryId'] !== 'new') {
        try {
          LoaderService.showLoader();
          this.categoryId = Number(params['categoryId']);
          delete this.categories;
          delete this.categoriesList;
          delete this.childrenCategories;
          delete this.category;
          delete this.products;
          delete this.productVariants;
          await Promise.all([
            this.findCategory(),
            this.findCategoryProducts(),
            this.findCategoriesList(),
            this.findCategoryProductVariants()
          ]);
          this.childrenCategories =
            this.categorySummary?.subcategoriesList || [];
          this.title.setTitle('Categoria ' + this.category?.displayName);
          this.activatedRoute.queryParams.subscribe((queryParams) => {
            if (queryParams['tab']) {
              this.selectedTab = Number(queryParams['tab']) || 0;
            } else {
              this.selectedTab = 0;
            }
          });
        } catch (error: any) {
          if (params['categoryId'] !== 'new') {
            AppDialogService.showErrorDialog(
              { message: 'Categoria não encontrada' },
              true
            );
          }
        }
      } else if (this.permission) {
        LoaderService.showLoader();
        await this.findCategoriesList();
        this.title.setTitle('Nova categoria');
      } else {
        this.router.navigate(['/unauthorized']);
      }
      LoaderService.showLoader(false);
    });
  }

  async findCategory(): Promise<void> {
    try {
      this.category = await lastValueFrom(
        this.productService
          .findProductCategoryById(this.categoryId as number)
          .pipe(map((data) => data.result))
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error, true);
    }
  }

  async findCategoryProducts(): Promise<void> {
    try {
      this.products = await lastValueFrom(
        this.productService
          .findProductCategoryProducts(this.categoryId as number)
          .pipe(
            map((data) =>
              data.result?.map((p) => ({
                ...p,
                brandEquityLabel: getEquityLabel(p.brandEquity as number)
              }))
            )
          )
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findCategoryProductVariants(): Promise<void> {
    try {
      this.productVariants = await lastValueFrom(
        this.productService
          .findProductVariantsListByCategoryId(this.categoryId as number)
          .pipe(
            map((data) =>
              data.result?.map((p) => ({
                ...p,
                brandEquityLabel: getEquityLabel(p.brandEquity as number)
              }))
            )
          )
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findCategoriesList(): Promise<void> {
    try {
      this.categories = await lastValueFrom(
        this.productService
          .findProductCategoriesTable()
          .pipe(map((data) => data.result))
      );
      this.categoriesList =
        this.categories?.reduce(
          (cats, c) => CategoryUtil.expandToList(c, null, cats),
          []
        ) || [];
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  tabChanged($event: number): void {
    this.router.navigate([`products/categories/${this.categoryId}`], {
      queryParams: { tab: $event },
      queryParamsHandling: 'merge'
    });
  }

  async updateCategory(category: Category): Promise<void> {
    this.category = category;
    this.cdRef.detectChanges();
    LoaderService.showLoader(false);
  }

  get categorySummary(): CategorySummaryDTO {
    return this.categoriesList?.find(
      (c) => c.categoryId === this.category?.categoryId
    );
  }
}
